import type { Observation } from "@medplum/fhirtypes";
import {
  asExamId,
  asFindingRecordId,
  asPatientId,
  asVisitId,
  type ExamFacts,
  parsePinnedCanonical,
} from "@repo/domain";
import {
  CHARTING_OBSERVATION_KIND_SYSTEM,
  codeSystemUrl,
  extensionUrl,
  OBSERVATION_CATEGORY_SYSTEM,
} from "@repo/fhir-systems";
import {
  EXAM_ENTITY_ID_SYSTEM,
  FINDING_ENTITY_ID_SYSTEM,
  PATIENT_ENTITY_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
} from "@repo/fhir-systems/identifiers";
import { oneOf, required } from "./read";
import { domainIdFromReference, logicalReferences, type ReferenceWriter } from "./references";

// ============================================================
// Exam ⇄ panel Observation, category exam
//
// hasMember refers to independently persisted findings. The panel has no
// clinical value or sign-off; preliminary/final records documentation close-out.
// ============================================================

export const EXAM_INSTANCE_PROFILE = extensionUrl("exam-panel");
export const EXAM_DEFINITION_URL = extensionUrl("exam-instantiates-canonical");
export const EXAM_LINEAGE_SYSTEM = codeSystemUrl("exam-lineage");
export const EXAM_TO_FHIR_STATUS = { preliminary: "preliminary", final: "final" } as const;

export function writeExamRow(
  facts: ExamFacts,
  prior: Observation | null,
  references: ReferenceWriter = logicalReferences,
): Observation {
  return {
    resourceType: "Observation",
    ...(prior
      ? {}
      : {
          meta: {
            profile: [EXAM_INSTANCE_PROFILE],
            tag: [{ system: CHARTING_OBSERVATION_KIND_SYSTEM, code: "exam-panel" }],
          },
        }),
    identifier: [{ system: EXAM_ENTITY_ID_SYSTEM, value: facts.id }],
    status: facts.status,
    category: [{ coding: [{ system: OBSERVATION_CATEGORY_SYSTEM, code: "exam" }] }],
    code: { coding: [{ system: EXAM_LINEAGE_SYSTEM, code: facts.definition.url }] },
    subject: references.identity("Patient", PATIENT_ENTITY_ID_SYSTEM, facts.patient),
    encounter: references.memberType("Encounter", VISIT_ENTITY_ID_SYSTEM, facts.visit),
    hasMember: facts.members.map((id) => references.identity("Observation", FINDING_ENTITY_ID_SYSTEM, id)),
    extension: [
      ...(prior?.extension ?? []).filter((extension) => extension.url !== EXAM_DEFINITION_URL),
      { url: EXAM_DEFINITION_URL, valueCanonical: `${facts.definition.url}|${facts.definition.version}` },
    ],
  };
}

export function readExamRow(row: Observation): ExamFacts {
  return {
    id: asExamId(required(row, "id", row.identifier?.find((id) => id.system === EXAM_ENTITY_ID_SYSTEM)?.value)),
    patient: asPatientId(required(row, "patient", domainIdFromReference(PATIENT_ENTITY_ID_SYSTEM, row.subject))),
    visit: asVisitId(required(row, "visit", domainIdFromReference(VISIT_ENTITY_ID_SYSTEM, row.encounter))),
    definition: required(
      row,
      "definition",
      parsePinnedCanonical(row.extension?.find((extension) => extension.url === EXAM_DEFINITION_URL)?.valueCanonical),
    ),
    status: oneOf(row, "status", row.status, ["preliminary", "final"] as const),
    members: (row.hasMember ?? []).map((ref) =>
      asFindingRecordId(required(row, "member", domainIdFromReference(FINDING_ENTITY_ID_SYSTEM, ref))),
    ),
  };
}
